<template>
  <div>
    <ContentCard>
      <template #header>
        <TabBar>
          <NavTabBarItem :is-selected="eventPeriod === 'upcoming'" :to="{ name: 'documents' }" data-testid="documents-tab-upcoming">
            {{ t('Bookings.upcoming') }}
          </NavTabBarItem>
          <NavTabBarItem :is-selected="eventPeriod === 'past'" :to="{ name: 'documentsArchive' }" data-testid="documents-tab-archive">
            {{ t('Bookings.archive') }}
          </NavTabBarItem>
        </TabBar>
      </template>

      <ProgressBar v-if="eventsLoading" data-testid="documents-loading" />
      <div v-if="events.length" data-testid="documents-list">
        <EventDocumentSection v-for="event in events" :key="event.id" ref="sectionRefs" :root-event="event" />
        <PaginationControl
          v-if="(pageInfo?.totalCount ?? 0) > limit"
          :total="pageInfo?.totalCount ?? 0"
          :page="page"
          :per-page="limit"
          :show-items-per-page="false"
          show-pagination="always"
          class="px-4 py-2"
          data-testid="documents-pagination"
          @update:page="page = $event"
        />
      </div>
      <div v-if="showEmptyState" data-testid="documents-empty-state" class="py-12 text-center">
        <h2 class="text-headline-s">{{ t('Documents.emptyStateTitle') }}</h2>
        <p class="mt-2 text-subtle">{{ t('Documents.emptyStateText') }}</p>
      </div>
    </ContentCard>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { EventPeriodEnum, useDocumentableEventsQuery } from '@/gql/myomr'
import { TabBar, NavTabBarItem } from '@/ui/Tabs'

import EventDocumentSection from './EventDocumentSection.vue'

const { t } = useI18n()

const route = useRoute()
const eventPeriod = computed<EventPeriodEnum>(() => (route.name == 'documentsArchive' ? EventPeriodEnum.Past : EventPeriodEnum.Upcoming))

const limit = 100
const page = ref(1)
const offset = computed(() => (page.value - 1) * limit)
const pageInfo = computed(() => eventsResult.value?.documentableEvents?.pageInfo)
const { result: eventsResult, loading: eventsLoading } = useDocumentableEventsQuery(() => ({
  period: eventPeriod.value,
  limit,
  offset: offset.value,
}))
const events = computed(() => eventsResult.value?.documentableEvents?.nodes ?? [])

const sectionRefs = ref<InstanceType<typeof EventDocumentSection>[]>([])

const showEmptyState = computed(() => {
  if (eventsLoading.value || !eventsResult.value) return false
  if (events.value.length === 0) return true
  if (sectionRefs.value.length !== events.value.length) return false
  if (sectionRefs.value.some((s) => s.loading)) return false
  return sectionRefs.value.every((s) => !s.hasDocumentables)
})
</script>
